import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Phone, Calendar, MapPin, FileText, Shield, Check, AlertTriangle, Loader2, Upload, X } from 'lucide-react';
import { ClientCreationFormData, ClientCreationResponse } from '@digitaltattoo/types';
import { apiClient } from '../utils/api';
import { uploadClientIdPhoto } from '../services/clientIdPhotosApi';
import { DateInput } from './DateInput';

interface ClientCreationFormProps {
  onSuccess: (response: ClientCreationResponse) => void;
  onCancel: () => void;
  isBackoffice: boolean;
}

export function ClientCreationForm({ onSuccess, onCancel, isBackoffice }: ClientCreationFormProps) {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState<ClientCreationFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    isMinor: null,
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'France'
    },
    identityDocument: {
      type: 'national_id',
      number: '',
      issueDate: '',
      expiryDate: '',
      issuePlace: '',
      issuingAuthority: ''
    },
    legalGuardian: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      relationshipToMinor: 'parent',
      relationshipDetails: '',
      identityDocument: {
        type: 'national_id',
        number: '',
        issueDate: '',
        expiryDate: '',
        issuePlace: '',
        issuingAuthority: ''
      }
    },
    consents: {
      dataProcessing: false,
      photography: false,
      marketing: false,
      minorConsent: false,
      guardianPresence: false
    }
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientDocumentFile, setClientDocumentFile] = useState<File | null>(null);
  const [guardianDocumentFile, setGuardianDocumentFile] = useState<File | null>(null);
  const [success, setSuccess] = useState<ClientCreationResponse | null>(null);
  const isMinor = formData.isMinor === true;
  const [duplicateCheckLoading, setDuplicateCheckLoading] = useState(false);

  const steps = [
    { id: 'basicInfo', label: t('clientForm.steps.basicInfo'), icon: User },
    { id: 'address', label: t('clientForm.steps.address'), icon: MapPin },
    { id: 'identity', label: t('clientForm.steps.identity'), icon: FileText },
    ...(isMinor ? [{ id: 'guardian', label: t('clientForm.steps.guardian'), icon: Shield }] : []),
    { id: 'consents', label: t('clientForm.steps.consents'), icon: Check }
  ];

  const finalStepIndex = steps.length - 1;

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const keys = field.split('.');
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Check if date of birth indicates minor
    if (field === 'dateOfBirth') {
      if (value) {
        checkIfMinor(value);
      } else {
        setFormData(prev => ({ ...prev, isMinor: null }));
      }
    }
  };

  const checkIfMinor = (dateOfBirth: string) => {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
   let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    const nextIsMinor = age < 18;
    setFormData(prev => ({ ...prev, isMinor: nextIsMinor }));
  };

  const handleFileUpload = (file: File | null, type: 'client' | 'guardian') => {
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          [`${type}Document`]: t('clientForm.validation.invalidFileType')
        }));
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          [`${type}Document`]: t('clientForm.validation.fileTooLarge')
        }));
        return;
      }

      // Clear any previous errors
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${type}Document`];
        return newErrors;
      });
    }

    if (type === 'client') {
      setClientDocumentFile(file);
    } else {
      setGuardianDocumentFile(file);
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      // Basic info validation
      if (!formData.firstName.trim()) {
        newErrors.firstName = t('clientForm.validation.firstNameRequired');
      }
      if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = t('clientForm.validation.emailInvalid');
      }
      if (formData.phone.trim() && formData.phone.replace(/\s+/g, '').length < 8) {
        newErrors.phone = t('clientForm.validation.phoneInvalid');
      }
    } else if (step === 1) {
      // Address fields are optional.
    } else if (step === 2) {
      // Identity document validation (optional - can be completed later)
      // No required validation for documents as they can be uploaded later
    } else if (step === 3 && isMinor && formData.legalGuardian) {
      if (formData.legalGuardian.email.trim() && !/\S+@\S+\.\S+/.test(formData.legalGuardian.email)) {
        newErrors['legalGuardian.email'] = t('clientForm.validation.emailInvalid');
      }
      if (formData.legalGuardian.phone.trim() && formData.legalGuardian.phone.replace(/\s+/g, '').length < 8) {
        newErrors['legalGuardian.phone'] = t('clientForm.validation.phoneInvalid');
      }
    } else if (step === finalStepIndex) {
      // Consent fields are optional during client record creation.
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkForDuplicates = async (): Promise<boolean> => {
    if (!formData.email.trim() && !formData.phone.trim()) return true;
    try {
      setDuplicateCheckLoading(true);
      const response = await apiClient('/api/clients/check-duplicate', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          phone: formData.phone
        })
      });

      const data = await response.json();
      
      if (data.exists) {
        setErrors(prev => ({
          ...prev,
          [data.field]: `Un client existe déjà avec cet${data.field === 'email' ? 'te adresse email' : ' numéro de téléphone'}`
        }));
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Duplicate check error:', error);
      setErrors(prev => ({
        ...prev,
        general: 'Erreur de vérification. Veuillez réessayer.'
      }));
      return false;
    } finally {
      setDuplicateCheckLoading(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    // Check for duplicates on first step
    if (currentStep === 0) {
      const noDuplicates = await checkForDuplicates();
      if (!noDuplicates) {
        return;
      }
    }

    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    // Validate final step
    if (!validateStep(finalStepIndex)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiClient('/api/clients/create', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        // Uploads were selected during creation but never sent with the JSON payload above
        if (clientDocumentFile && data.data?.client?.id) {
          try {
            await uploadClientIdPhoto(data.data.client.id, clientDocumentFile, 'front');
          } catch (uploadError) {
            console.error('Client document upload error:', uploadError);
          }
        }
        setSuccess(data);
        onSuccess(data);
      } else {
        if (data.errors) {
          const errorMap: Record<string, string> = {};
          data.errors.forEach((error: any) => {
            errorMap[error.field] = error.message;
          });
          setErrors(errorMap);
        } else {
          setErrors({ general: data.message || t('clientForm.error.networkError') });
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      setErrors({ general: t('clientForm.error.networkError') });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-display font-bold text-textPrimary mb-4">
            {t('clientForm.success.title')}
          </h2>
          
          <p className="text-textSecondary mb-8">
            {t('clientForm.success.message', { 
              name: `${success.data?.client.firstName} ${success.data?.client.lastName}` 
            })}
          </p>

          {/* Client Info */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-textPrimary mb-4">Informations Client</h3>
            <div className="space-y-2 text-sm">
              <p><strong>Nom:</strong> {success.data?.client.firstName} {success.data?.client.lastName}</p>
              <p><strong>Email:</strong> {success.data?.client.email}</p>
              <p><strong>Téléphone:</strong> {success.data?.client.phone}</p>
              {success.data?.client.isMinor && <p className="text-orange-600"><strong>Statut:</strong> Mineur</p>}
            </div>
          </div>

          {/* Legal Guardian Info */}
          {isMinor && formData.legalGuardian && success.data?.legalGuardian && (
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-textPrimary mb-4">{t('clientForm.success.guardianInfo')}</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Nom:</strong> {success.data.legalGuardian.firstName} {success.data.legalGuardian.lastName}</p>
                <p><strong>Email:</strong> {success.data.legalGuardian.email}</p>
                <p><strong>Téléphone:</strong> {success.data.legalGuardian.phone}</p>
              </div>
              <p className="text-blue-600 text-sm mt-4">{t('clientForm.success.guardianNotified')}</p>
            </div>
          )}

          {/* QR Code */}
          {success.data?.qrCode && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-textPrimary mb-4">{t('clientForm.success.qrCode')}</h3>
              <div className="flex justify-center mb-4">
                <img 
                  src={success.data.qrCode.dataUrl} 
                  alt="Client QR Code" 
                  className="w-32 h-32 border border-gray-300 rounded"
                />
              </div>
              <p className="text-sm text-textSecondary">
                Code d'accès rapide au profil client
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                setSuccess(null);
                setFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  dateOfBirth: '',
                  isMinor: null,
                  address: { street: '', city: '', postalCode: '', country: 'France' },
                  identityDocument: { type: 'national_id', number: '', issueDate: '', expiryDate: '', issuePlace: '', issuingAuthority: '' },
                  legalGuardian: { firstName: '', lastName: '', email: '', phone: '', relationshipToMinor: 'parent', relationshipDetails: '', identityDocument: { type: 'national_id', number: '', issueDate: '', expiryDate: '', issuePlace: '', issuingAuthority: '' } },
                  consents: { dataProcessing: false, photography: false, marketing: false, minorConsent: false, guardianPresence: false }
                });
                setCurrentStep(0);
                setClientDocumentFile(null);
                setGuardianDocumentFile(null);
              }}
              className="btn-primary"
            >
              {t('clientForm.success.createAnother')}
            </button>
            <button
              onClick={onCancel}
              className="btn-secondary"
            >
              {isBackoffice ? t('clientForm.success.backToDashboard') : t('clientForm.success.backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Progress Steps */}
      <div className="mb-12">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-colors ${
                index <= currentStep 
                  ? 'bg-primary-300 border-primary-300 text-white' 
                  : 'bg-white border-gray-300 text-gray-400'
              }`}>
                <step.icon className="w-5 h-5" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className={`text-xs lg:text-sm font-medium whitespace-nowrap ${
                  index <= currentStep ? 'text-primary-600' : 'text-gray-500'
                }`}>
                  {step.label}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className={`hidden sm:block w-16 h-0.5 mx-4 ${
                  index < currentStep ? 'bg-primary-300' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
        {/* Error Messages */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-800 font-medium">{errors.general}</span>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div>
          {/* Step 0: Basic Information */}
          {currentStep === 0 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-display font-bold text-textPrimary mb-2">
                  {t('clientForm.steps.basicInfo')}
                </h2>
                <p className="text-textSecondary">
                  Informations personnelles du client
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.firstName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors.firstName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.firstName')}
                  />
                  {errors.firstName && <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.lastName')} (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors.lastName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.lastName')}
                  />
                  {errors.lastName && <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    {t('clientForm.fields.email')} (optionnel)
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.email')}
                  />
                  {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    {t('clientForm.fields.phone')} (optionnel)
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors.phone ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.phone')}
                  />
                  {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    {t('clientForm.fields.dateOfBirth')} (optionnel)
                  </label>
                 <DateInput
                  value={formData.dateOfBirth}
                  disabled={formData.isMinor === true && !formData.dateOfBirth}
                  onChange={(value) => handleInputChange('dateOfBirth', value)}
                  className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                    errors.dateOfBirth ? 'border-red-300' : 'border-gray-300'
                  }`}
                  max={new Date().toISOString().split('T')[0]}
                />
                  {errors.dateOfBirth && <p className="text-red-600 text-sm mt-1">{errors.dateOfBirth}</p>}
                  {isMinor && (
                    <p className="text-orange-600 text-sm mt-2 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {t('clientForm.minorDetected')}
                    </p>
                  )}
                  <label className="mt-3 flex items-center gap-2 text-sm text-textPrimary">
                    <input
                      type="checkbox"
                      checked={isMinor}
                      disabled={Boolean(formData.dateOfBirth)}
                      onChange={(e) => {
                        handleInputChange('isMinor', e.target.checked ? true : null);
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600"
                    />
                    Mineur (optionnel)
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Address */}
          {currentStep === 1 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-display font-bold text-textPrimary mb-2">
                  {t('clientForm.steps.address')}
                </h2>
                <p className="text-textSecondary">
                  Adresse de résidence du client
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.street')} (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.address.street}
                    onChange={(e) => handleInputChange('address.street', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors['address.street'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.street')}
                  />
                  {errors['address.street'] && <p className="text-red-600 text-sm mt-1">{errors['address.street']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.city')} (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors['address.city'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.city')}
                  />
                  {errors['address.city'] && <p className="text-red-600 text-sm mt-1">{errors['address.city']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.postalCode')} (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.address.postalCode}
                    onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors['address.postalCode'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.postalCode')}
                  />
                  {errors['address.postalCode'] && <p className="text-red-600 text-sm mt-1">{errors['address.postalCode']}</p>}
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.country')} (optionnel)
                  </label>
                  <select
                    value={formData.address.country}
                    onChange={(e) => handleInputChange('address.country', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors['address.country'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="France">France</option>
                    <option value="Belgium">Belgique</option>
                    <option value="Switzerland">Suisse</option>
                    <option value="Canada">Canada</option>
                    <option value="Other">Autre</option>
                  </select>
                  {errors['address.country'] && <p className="text-red-600 text-sm mt-1">{errors['address.country']}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Identity Document */}
          {currentStep === 2 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-display font-bold text-textPrimary mb-2">
                  {t('clientForm.steps.identity')}
                </h2>
                <p className="text-textSecondary">
                  Pièce d'identité du client (optionnel - peut être fournie plus tard)
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.documentType')}
                  </label>
                  <select
                    value={formData.identityDocument.type}
                    onChange={(e) => handleInputChange('identityDocument.type', e.target.value)}
                    className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                  >
                    <option value="national_id">{t('clientForm.documentTypes.nationalId')}</option>
                    <option value="passport">{t('clientForm.documentTypes.passport')}</option>
                    <option value="driving_license">{t('clientForm.documentTypes.drivingLicense')}</option>
                    <option value="birth_certificate">{t('clientForm.documentTypes.birthCertificate')}</option>
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.documentNumber')}
                  </label>
                  <input
                    type="text"
                    value={formData.identityDocument.number}
                    onChange={(e) => handleInputChange('identityDocument.number', e.target.value)}
                    className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                    placeholder={t('clientForm.placeholders.documentNumber')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.issueDate')}
                  </label>
                  <DateInput
  value={formData.identityDocument.issueDate}
  onChange={(value) => handleInputChange('identityDocument.issueDate', value)}
  className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
  max={new Date().toISOString().split('T')[0]}
/>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.expiryDate')}
                  </label>
                  <DateInput
  value={formData.identityDocument.expiryDate}
  onChange={(value) => handleInputChange('identityDocument.expiryDate', value)}
  className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
  min={new Date().toISOString().split('T')[0]}
/>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.issuePlace')}
                  </label>
                  <input
                    type="text"
                    value={formData.identityDocument.issuePlace}
                    onChange={(e) => handleInputChange('identityDocument.issuePlace', e.target.value)}
                    className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                    placeholder={t('clientForm.placeholders.issuePlace')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.issuingAuthority')}
                  </label>
                  <input
                    type="text"
                    value={formData.identityDocument.issuingAuthority}
                    onChange={(e) => handleInputChange('identityDocument.issuingAuthority', e.target.value)}
                    className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                    placeholder={t('clientForm.placeholders.issuingAuthority')}
                  />
                </div>
              </div>

              {/* Document Upload Section */}
              <div className="space-y-6">
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-textPrimary mb-4">
                    {t('clientForm.documentUpload.title')}
                  </h3>
                  <p className="text-sm text-textSecondary mb-4">
                    {t('clientForm.documentUpload.description')}
                  </p>

                  {/* File Upload */}
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                      <input
                        type="file"
                        id="clientDocument"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => handleFileUpload(e.target.files?.[0] || null, 'client')}
                        className="hidden"
                      />
                      <label htmlFor="clientDocument" className="cursor-pointer">
                        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <div className="text-sm">
                          <span className="font-medium text-primary-600 hover:text-primary-500">
                            {t('clientForm.documentUpload.selectFile')}
                          </span>
                        </div>
                      </label>
                      {clientDocumentFile && (
                        <div className="mt-4 flex items-center justify-center space-x-2">
                          <FileText className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-green-600">
                            {t('clientForm.documentUpload.fileSelected')}: {clientDocumentFile.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleFileUpload(null, 'client')}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    {errors.clientDocument && (
                      <p className="text-red-600 text-sm">{errors.clientDocument}</p>
                    )}
                  </div>

                  {/* Complete Later Option */}
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="completeLater"
                        checked={!clientDocumentFile}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFileUpload(null, 'client');
                          }
                        }}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <div>
                        <label htmlFor="completeLater" className="text-sm font-medium text-blue-800">
                          {t('clientForm.completeLater.title')}
                        </label>
                        <p className="text-sm text-blue-700 mt-1">
                          {t('clientForm.completeLater.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Legal Guardian (only if minor) */}
          {currentStep === 3 && isMinor && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-display font-bold text-textPrimary mb-2">
                  {t('clientForm.steps.guardian')}
                </h2>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-orange-800">
                        {t('clientForm.guardianRequired')}
                      </h3>
                      <p className="text-sm text-orange-700 mt-1">
                        {t('clientForm.guardianRequiredDescription')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.guardianFirstName')} (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.legalGuardian?.firstName || ''}
                    onChange={(e) => handleInputChange('legalGuardian.firstName', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors['legalGuardian.firstName'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.guardianFirstName')}
                  />
                  {errors['legalGuardian.firstName'] && <p className="text-red-600 text-sm mt-1">{errors['legalGuardian.firstName']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.guardianLastName')} (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.legalGuardian?.lastName || ''}
                    onChange={(e) => handleInputChange('legalGuardian.lastName', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors['legalGuardian.lastName'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.guardianLastName')}
                  />
                  {errors['legalGuardian.lastName'] && <p className="text-red-600 text-sm mt-1">{errors['legalGuardian.lastName']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    {t('clientForm.fields.guardianEmail')} (optionnel)
                  </label>
                  <input
                    type="email"
                    value={formData.legalGuardian?.email || ''}
                    onChange={(e) => handleInputChange('legalGuardian.email', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors['legalGuardian.email'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.guardianEmail')}
                  />
                  {errors['legalGuardian.email'] && <p className="text-red-600 text-sm mt-1">{errors['legalGuardian.email']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    {t('clientForm.fields.guardianPhone')} (optionnel)
                  </label>
                  <input
                    type="tel"
                    value={formData.legalGuardian?.phone || ''}
                    onChange={(e) => handleInputChange('legalGuardian.phone', e.target.value)}
                    className={`w-full px-4 py-4 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200 ${
                      errors['legalGuardian.phone'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('clientForm.placeholders.guardianPhone')}
                  />
                  {errors['legalGuardian.phone'] && <p className="text-red-600 text-sm mt-1">{errors['legalGuardian.phone']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.relationship')} (optionnel)
                  </label>
                  <select
                    value={formData.legalGuardian?.relationshipToMinor || 'parent'}
                    onChange={(e) => handleInputChange('legalGuardian.relationshipToMinor', e.target.value)}
                    className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                  >
                    <option value="parent">{t('clientForm.relationships.parent')}</option>
                    <option value="legal_guardian">{t('clientForm.relationships.legalGuardian')}</option>
                    <option value="other">{t('clientForm.relationships.other')}</option>
                  </select>
                </div>

                {formData.legalGuardian?.relationshipToMinor === 'other' && (
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('clientForm.fields.relationshipDetails')}
                    </label>
                    <input
                      type="text"
                      value={formData.legalGuardian?.relationshipDetails || ''}
                      onChange={(e) => handleInputChange('legalGuardian.relationshipDetails', e.target.value)}
                      className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                      placeholder={t('clientForm.placeholders.relationshipDetails')}
                    />
                  </div>
                )}
              </div>

              {/* Guardian Document Section */}
              <div className="space-y-6">
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-textPrimary mb-4">
                    {t('clientForm.guardianDocument.title')}
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('clientForm.fields.documentType')}
                      </label>
                      <select
                        value={formData.legalGuardian?.identityDocument?.type || 'national_id'}
                        onChange={(e) => handleInputChange('legalGuardian.identityDocument.type', e.target.value)}
                        className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                      >
                        <option value="national_id">{t('clientForm.documentTypes.nationalId')}</option>
                        <option value="passport">{t('clientForm.documentTypes.passport')}</option>
                        <option value="driving_license">{t('clientForm.documentTypes.drivingLicense')}</option>
                      </select>
                    </div>

                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('clientForm.fields.documentNumber')}
                      </label>
                      <input
                        type="text"
                        value={formData.legalGuardian?.identityDocument?.number || ''}
                        onChange={(e) => handleInputChange('legalGuardian.identityDocument.number', e.target.value)}
                        className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                        placeholder={t('clientForm.placeholders.documentNumber')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('clientForm.fields.issueDate')}
                      </label>
                      <DateInput
                        value={formData.legalGuardian?.identityDocument?.issueDate || ''}
                        onChange={(value) =>
                          handleInputChange('legalGuardian.identityDocument.issueDate', value)
                        }
                        className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                        max={new Date().toISOString().split('T')[0]}
                      />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-2">
                          {t('clientForm.fields.expiryDate')}
                        </label>

                        <DateInput
                          value={formData.legalGuardian?.identityDocument?.expiryDate || ''}
                          onChange={(value) =>
                            handleInputChange('legalGuardian.identityDocument.expiryDate', value)
                          }
                          className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                          min={new Date().toISOString().split('T')[0]}
                        />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('clientForm.fields.issuePlace')}
                      </label>
                      <input
                        type="text"
                        value={formData.legalGuardian?.identityDocument?.issuePlace || ''}
                        onChange={(e) => handleInputChange('legalGuardian.identityDocument.issuePlace', e.target.value)}
                        className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                        placeholder={t('clientForm.placeholders.issuePlace')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('clientForm.fields.issuingAuthority')}
                      </label>
                      <input
                        type="text"
                        value={formData.legalGuardian?.identityDocument?.issuingAuthority || ''}
                        onChange={(e) => handleInputChange('legalGuardian.identityDocument.issuingAuthority', e.target.value)}
                        className="w-full px-4 py-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all duration-200"
                        placeholder={t('clientForm.placeholders.issuingAuthority')}
                      />
                    </div>
                  </div>

                  {/* Guardian Document Upload */}
                  <div className="space-y-6">
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-medium text-textPrimary mb-4">
                        {t('clientForm.guardianDocumentUpload.title')}
                      </h3>
                      <p className="text-sm text-textSecondary mb-4">
                        {t('clientForm.guardianDocumentUpload.description')}
                      </p>

                      {/* File Upload */}
                      <div className="space-y-4">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                          <input
                            type="file"
                            id="guardianDocument"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => handleFileUpload(e.target.files?.[0] || null, 'guardian')}
                            className="hidden"
                          />
                          <label htmlFor="guardianDocument" className="cursor-pointer">
                            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <div className="text-sm">
                              <span className="font-medium text-primary-600 hover:text-primary-500">
                                {t('clientForm.documentUpload.selectFile')}
                              </span>
                            </div>
                          </label>
                          {guardianDocumentFile && (
                            <div className="mt-4 flex items-center justify-center space-x-2">
                              <FileText className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-green-600">
                                {t('clientForm.documentUpload.fileSelected')}: {guardianDocumentFile.name}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleFileUpload(null, 'guardian')}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                        {errors.guardianDocument && (
                          <p className="text-red-600 text-sm">{errors.guardianDocument}</p>
                        )}
                      </div>

                      {/* Complete Later Option */}
                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            id="guardianCompleteLater"
                            checked={!guardianDocumentFile}
                            onChange={(e) => {
                              if (e.target.checked) {
                                handleFileUpload(null, 'guardian');
                              }
                            }}
                            className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <div>
                            <label htmlFor="guardianCompleteLater" className="text-sm font-medium text-blue-800">
                              {t('clientForm.guardianCompleteLater.title')}
                            </label>
                            <p className="text-sm text-blue-700 mt-1">
                              {t('clientForm.guardianCompleteLater.description')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Final Step: Consents */}
          {currentStep === finalStepIndex && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-display font-bold text-textPrimary mb-2">
                  {t('clientForm.steps.consents')}
                </h2>
                <p className="text-textSecondary">
                  Consentements requis pour la création du compte
                </p>
              </div>

              <div className="space-y-6">
                {/* Data Processing Consent */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.consents.dataProcessing}
                      onChange={(e) => handleInputChange('consents.dataProcessing', e.target.checked)}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div>
                      <div className="text-sm font-medium text-textPrimary">
                        {t('clientForm.consents.dataProcessing.title')} (optionnel)
                      </div>
                      <div className="text-sm text-textSecondary mt-1">
                        {t('clientForm.consents.dataProcessing.description')}
                      </div>
                    </div>
                  </label>
                  {errors['consents.dataProcessing'] && (
                    <p className="text-red-600 text-sm mt-2">{errors['consents.dataProcessing']}</p>
                  )}
                </div>

                {/* Photography Consent */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.consents.photography}
                      onChange={(e) => handleInputChange('consents.photography', e.target.checked)}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div>
                      <div className="text-sm font-medium text-textPrimary">
                        {t('clientForm.consents.photography.title')}
                      </div>
                      <div className="text-sm text-textSecondary mt-1">
                        {t('clientForm.consents.photography.description')}
                      </div>
                    </div>
                  </label>
                </div>

                {/* Marketing Consent */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.consents.marketing}
                      onChange={(e) => handleInputChange('consents.marketing', e.target.checked)}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div>
                      <div className="text-sm font-medium text-textPrimary">
                        {t('clientForm.consents.marketing.title')}
                      </div>
                      <div className="text-sm text-textSecondary mt-1">
                        {t('clientForm.consents.marketing.description')}
                      </div>
                    </div>
                  </label>
                </div>

                {/* Minor-specific consents */}
                {isMinor && (
                  <>
                    <div className="border border-orange-200 bg-orange-50 rounded-lg p-6">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.consents.minorConsent}
                          onChange={(e) => handleInputChange('consents.minorConsent', e.target.checked)}
                          className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <div>
                          <div className="text-sm font-medium text-orange-800">
                            {t('clientForm.consents.minorConsent.title')} (optionnel)
                          </div>
                          <div className="text-sm text-orange-700 mt-1">
                            {t('clientForm.consents.minorConsent.description')}
                          </div>
                        </div>
                      </label>
                      {errors['consents.minorConsent'] && (
                        <p className="text-red-600 text-sm mt-2">{errors['consents.minorConsent']}</p>
                      )}
                    </div>

                    <div className="border border-orange-200 bg-orange-50 rounded-lg p-6">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.consents.guardianPresence}
                          onChange={(e) => handleInputChange('consents.guardianPresence', e.target.checked)}
                          className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <div>
                          <div className="text-sm font-medium text-orange-800">
                            {t('clientForm.consents.guardianPresence.title')} (optionnel)
                          </div>
                          <div className="text-sm text-orange-700 mt-1">
                            {t('clientForm.consents.guardianPresence.description')}
                          </div>
                        </div>
                      </label>
                      {errors['consents.guardianPresence'] && (
                        <p className="text-red-600 text-sm mt-2">{errors['consents.guardianPresence']}</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center pt-8 border-t border-gray-200">
          <button
            type="button"
            onClick={currentStep === 0 ? onCancel : handlePrevious}
            className="btn-secondary"
          >
            {currentStep === 0 ? t('clientForm.cancel') : t('clientForm.previous')}
          </button>

          <div className="flex items-center space-x-4">
            {duplicateCheckLoading && (
              <div className="flex items-center space-x-2 text-primary-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Vérification...</span>
              </div>
            )}
            
            {currentStep < finalStepIndex ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={duplicateCheckLoading}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('clientForm.next')}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>{isSubmitting ? t('clientForm.creating') : t('clientForm.createAccount')}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}